import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  hideSubtitleOnMobile?: boolean;
  icon: LucideIcon;
  tone?: "default" | "warning" | "success" | "danger" | "info";
  trend?: { value: string; positive: boolean };
  tooltip?: string;
  href?: string;
  className?: string;
  action?: ReactNode;
}

const toneStyles: Record<NonNullable<KpiCardProps["tone"]>, { card: string; iconWrap: string; icon: string; title: string }> = {
  default: {
    card: "bg-card",
    iconWrap: "bg-secondary",
    icon: "text-muted-foreground",
    title: "text-muted-foreground",
  },
  warning: {
    card: "border-warning/25 bg-gradient-to-br from-warning/8 via-card to-card",
    iconWrap: "bg-gradient-to-br from-warning/20 to-warning/10 ring-1 ring-warning/20",
    icon: "text-warning",
    title: "text-warning/90",
  },
  success: {
    card: "border-emerald-500/25 bg-gradient-to-br from-emerald-500/8 via-card to-card",
    iconWrap: "bg-gradient-to-br from-emerald-500/20 to-emerald-500/10 ring-1 ring-emerald-500/20",
    icon: "text-emerald-600",
    title: "text-emerald-700",
  },
  danger: {
    card: "border-destructive/25 bg-gradient-to-br from-destructive/8 via-card to-card",
    iconWrap: "bg-gradient-to-br from-destructive/20 to-destructive/10 ring-1 ring-destructive/20",
    icon: "text-destructive",
    title: "text-destructive/90",
  },
  info: {
    card: "border-info/25 bg-gradient-to-br from-info/8 via-card to-card",
    iconWrap: "bg-gradient-to-br from-info/20 to-info/10 ring-1 ring-info/20",
    icon: "text-info",
    title: "text-info/90",
  },
};

export function KpiCard({
  title,
  value,
  subtitle,
  hideSubtitleOnMobile = false,
  icon: Icon,
  tone = "default",
  trend,
  tooltip,
  href,
  className,
  action,
}: KpiCardProps) {
  const navigate = useNavigate();
  const styles = toneStyles[tone];

  const card = (
    <div
      className={cn(
        "rounded-xl border p-5 shadow-sm transition-all duration-200",
        styles.card,
        href && "cursor-pointer hover:shadow-md hover:border-accent/40 hover:-translate-y-0.5",
        className
      )}
      onClick={href ? () => navigate(href) : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className={cn("text-sm font-medium", styles.title)}>{title}</p>
          <p className="text-2xl font-bold tracking-tight text-card-foreground">{value}</p>
          {subtitle && (
            <p className={cn("text-xs text-muted-foreground", hideSubtitleOnMobile && "hidden sm:block")}>
              {subtitle}
            </p>
          )}
          {trend && (
            <p className={cn("text-xs font-medium", trend.positive ? "text-success" : "text-destructive")}>
              {trend.positive ? "↑" : "↓"} {trend.value}
            </p>
          )}
          {action ? <div className="pt-1">{action}</div> : null}
        </div>
        <div className={cn("rounded-2xl p-3.5 shadow-sm", styles.iconWrap)}>
          <Icon className={cn("h-7 w-7", styles.icon)} />
        </div>
      </div>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent><p>{tooltip}</p></TooltipContent>
      </Tooltip>
    );
  }

  return card;
}
